import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AllCommunityModule, ColDef, FilterModel, GridReadyEvent, ModuleRegistry, RowSelectionOptions, themeQuartz } from 'ag-grid-community';
import { TextInputComponent } from '../text-input/text-input.component';
import { ButtonComponent } from '../button/button.component';
import { CommonModule, NgClass } from '@angular/common';
import { GlobalService } from '../../services/global.service';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';
import { PluralPipe } from '../../pipes/plural.pipe';
import { ActivatedRoute } from '@angular/router';
const myTheme = themeQuartz.withParams({
  // ---------- BASIC ----------
  accentColor: "#ffffff",
  backgroundColor: "#FFFFFF",
  chromeBackgroundColor: { ref: "backgroundColor" },
  foregroundColor: "#555B62",
  borderColor: "#E3E8EF",

  // ---------- HEADER ----------
  headerBackgroundColor: "#FFFFFF",
  headerTextColor: "#3F41D1",
  headerFontSize: 15,
  headerFontWeight: 600,
  headerHeight: 50,


  // headerColumnSeparatorWidth: 0,
  columnBorder: false,

  // ---------- BODY ----------
  rowBorder: true,
  rowVerticalPaddingScale: 2,
  cellHorizontalPaddingScale: 0.9,
  fontFamily: { googleFont: "arial" },
  fontSize: 15,

  // ---------- OUTER WRAPPER ----------
  wrapperBorder: false,
  wrapperBorderRadius: 10,
  spacing: 6,

  // ---------- CHECKBOX ----------
  checkboxBorderRadius: 2,
  checkboxCheckedBorderColor: "#1453a0ff",
  checkboxCheckedBackgroundColor: "#3F41D1",
  checkboxCheckedShapeColor: "#FFFFFF",

  // ---------- FILTER BUTTON ----------
  filterPanelApplyButtonBackgroundColor: "#087AD1",
  filterPanelCardSubtleHoverColor: "#065aab",

  // ---------- PAGINATION ----------
  inputBackgroundColor: "#ffffff",
});




ModuleRegistry.registerModules([AllCommunityModule]); // Angular Data Grid Component

@Component({
  selector: 'app-ag-grid-list',
  imports: [AgGridAngular, ButtonComponent, CommonModule, ConfirmationPopupComponent, NgClass, PluralPipe],
  templateUrl: './ag-grid-list.component.html',
  styleUrl: './ag-grid-list.component.css'
})
export class AgGridListComponent {
  @ViewChild('grid') grid!: AgGridAngular;
  @ViewChild('bulk_delete_confirmation') bulk_delete_confirmation!: ConfirmationPopupComponent;

  get rowSelection(): RowSelectionOptions {
    return {
      mode: 'multiRow',
      enableClickSelection: false,
      headerCheckbox: this.show_checkboxes,
      checkboxes: this.show_checkboxes,
    };
  }
  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    flex: 1,
    minWidth: 120,
    filterParams: {
      filterOptions: ['contains'],
      defaultOption: 'contains',
      suppressAndOrCondition: true
    },
    cellStyle: {
      display: 'flex',
      alignItems: 'center'
    }
  };



  @Input() list: any;
  @Input() colDefs: ColDef[] = []
  @Input() loading = ''
  @Input() itemName = 'Item'
  @Input() params: any = {}
  @Input() main_description = ''
  @Input() description = ''
  @Input() bulk_delete: boolean = true;
  @Input() show_checkboxes: boolean = true;
  @Input() getRowStyle: ((params: any) => any) | undefined = undefined;
  @Input() height: string = 'calc(100vh - 200px)';

  @Output() fetch_new_list = new EventEmitter<any>();
  @Output() modified = new EventEmitter<any>();
  @Output() bulkDelete = new EventEmitter<number[]>();

  theme = myTheme

  overlayNoRowsTemplate = '';



  gridApi: any;

  latest_filter: any;

  pagination = {
    page: 1,
    page_size: 10,
  }
  total_pages = 1

  pending_delete_ids: number[] = [];
  selected_ids_count = 0;
  selected_ids: any = [];

  constructor(public gs: GlobalService, public ar: ActivatedRoute) { }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['list'] && this.list) {
      this.total_pages = Math.ceil(this.list.count / this.pagination.page_size)
    }
    if (changes['colDefs'] && this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.colDefs);
    }
  }

  ngOnInit(): void {
    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params }
      console.log(this.params, 'params');
      this.pagination.page = 1;
    })
    this.overlayNoRowsTemplate = `
  <div class="flex flex-col items-center justify-center h-full">
  ${this.main_description != '' || this.description != '' ? '' : '<img  src="assets/no-records.gif" alt="No data" class="w-40 h-auto opacity-80"/>'}
    <span class="mt-4 text-gray-900 text-xl font-semibold"> ${this.main_description}</span>
    <p class="mt-4 text-gray-500 text-base">
      ${this.description != '' ? this.description : 'No Records found'}
    </p>
  </div>
`;
  }

  latest_sort_by: string | undefined;
  latest_sort_order: string | undefined;

  onSortChanged(event: any) {
    const sortState = this.gridApi.getColumnState().filter((s: any) => s.sort);

    if (sortState.length > 0) {
      const primarySort = sortState[0];
      this.latest_sort_by = primarySort.colId;
      this.latest_sort_order = primarySort.sort?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    } else {
      this.latest_sort_by = undefined;
      this.latest_sort_order = undefined;
    }

    this.pagination.page = 1; // Reset to first page when sorting
    this.fetch_new_list.emit(this.get_query_params());
  }

  onFilterChanged() {
    const filterModel = this.grid.api.getFilterModel();
    const mongo_filter_object = agGridFilterToMongo(filterModel);
    this.latest_filter = mongo_filter_object;
    this.pagination.page = 1; // Reset to first page when filtering
    this.fetch_new_list.emit(this.get_query_params());
  }

  grid_ready(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.fetch_new_list.emit(this.get_query_params())
  }

  getVisibleRowCount(): number {
    const rowHeight = this.gridApi.getSizesForCurrentTheme().rowHeight;
    const { top, bottom } = this.gridApi.getVerticalPixelRange();
    const viewportPx = bottom - top;
    return Math.floor(viewportPx / rowHeight);
  }

  change_pagination(direction: string) {
    if (direction == 'next') {
      if (this.pagination.page == this.total_pages) {
        return
      }
      this.pagination.page = this.pagination.page + 1
    } else if (direction == 'previous') {
      if (this.pagination.page == 1) {
        return
      }
      this.pagination.page = this.pagination.page - 1
    } else if (direction == 'first') {
      if (this.pagination.page == 1) {
        return
      }
      this.pagination.page = 1
    } else if (direction == 'last') {
      if (this.pagination.page == this.total_pages) {
        return
      }
      this.pagination.page = this.total_pages
    }
    this.fetch_new_list.emit(this.get_query_params())

  }

  get_query_params() {
    return {
      ...this.params,
      page: this.pagination.page,
      page_size: this.pagination.page_size,
      skip: (this.pagination.page - 1) * this.pagination.page_size,
      limit: this.pagination.page_size,

      sort_by: this.latest_sort_by,
      sort_order: this.latest_sort_order,
      filter: this.latest_filter,

    };
  }

  cell_changed(event: any) {
    this.modified.emit(event.data);
  }

  delete_selected_rows() {
    const selected_item = this.grid.api.getSelectedNodes();
    const selected_data = selected_item.map(item => item.data);
    const selected_ids = selected_data.map(item => item.id || item.user_id);

    if (selected_ids.length === 0) {
      this.gs.toastr_shows_function(`Please select at least one ${this.itemName} to delete.`, 'Warning', 'warning');
      return;
    }

    this.pending_delete_ids = selected_ids;
    this.selected_ids_count = selected_ids.length;
    this.selected_ids = selected_ids;
    this.bulk_delete_confirmation.open();
  }

  confirm_bulk_delete() {
    this.bulkDelete.emit(this.pending_delete_ids);
    this.bulk_delete_confirmation.close();
    this.pending_delete_ids = [];
    this.selected_ids_count = 0;
  }

  onSelectionChanged(event: any) {
    this.selected_ids_count = this.gridApi.getSelectedRows().length;
    const selected_item = this.grid.api.getSelectedNodes();
    const selected_data = selected_item.map(item => item.data);
    const selected_ids = selected_data.map(item => item.id || item.user_id);
    this.selected_ids = selected_ids;
  }
}
function agGridFilterToMongo(filterModel: FilterModel) {
  throw new Error('Function not implemented.');
}

